import { useEffect, useState } from 'react';
import { Save, Sliders } from 'lucide-react';
import { Card, SectionHeader, Button, Badge } from '@/components/ui';
import { supabase } from '@/lib/supabase';

type SettingRow = { key: string; value: unknown };

export default function SettingsPage() {
  const [rows, setRows] = useState<SettingRow[]>([]);
  const [threshold, setThreshold] = useState(0.82);
  const [autoPublish, setAutoPublish] = useState(false);
  const [adminReview, setAdminReview] = useState(true);
  const [showElite, setShowElite] = useState(() => {
    try {
      const saved = localStorage.getItem('localhub_show_elite_featured');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });
  const [adminEmail, setAdminEmail] = useState(() => localStorage.getItem('localhub_admin_email') || 'admin@zhasearch.com');
  const [adminPassword, setAdminPassword] = useState(() => localStorage.getItem('localhub_admin_password') || 'SuperAdmin@2026');

  const [image, setImage] = useState({ webp: true, avif: true, thumbnail: true, blur_placeholder: true });
  const [seo, setSeo] = useState({ generate_faq: true, generate_schema_org: true, generate_sitemap: true, generate_rss: true });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void supabase.from('settings').select('*').then(({ data }) => {
      const r = (data as SettingRow[]) ?? [];
      setRows(r);
      const t = r.find((x) => x.key === 'duplicate_threshold')?.value;
      if (typeof t === 'number') setThreshold(t);
      const a = r.find((x) => x.key === 'auto_publish')?.value;
      if (typeof a === 'boolean') setAutoPublish(a);
      const ar = r.find((x) => x.key === 'admin_review_required')?.value;
      if (typeof ar === 'boolean') setAdminReview(ar);
      const im = r.find((x) => x.key === 'image_optimization')?.value;
      if (im && typeof im === 'object') setImage((prev) => ({ ...prev, ...(im as typeof image) }));
      const se = r.find((x) => x.key === 'seo')?.value;
      if (se && typeof se === 'object') setSeo((prev) => ({ ...prev, ...(se as typeof seo) }));
      const el = r.find((x) => x.key === 'show_elite_featured')?.value;
      if (typeof el === 'boolean') setShowElite(el);
      const ae = r.find((x) => x.key === 'admin_email')?.value;
      if (ae && typeof ae === 'string') setAdminEmail(ae);
      const ap = r.find((x) => x.key === 'admin_password')?.value;
      if (ap && typeof ap === 'string') setAdminPassword(ap);
    });
  }, []);

  const save = async () => {
    localStorage.setItem('localhub_show_elite_featured', JSON.stringify(showElite));
    localStorage.setItem('localhub_admin_email', adminEmail);
    localStorage.setItem('localhub_admin_password', adminPassword);

    await supabase.from('settings').upsert([
      { key: 'duplicate_threshold', value: threshold, updated_at: new Date().toISOString() },
      { key: 'auto_publish', value: autoPublish, updated_at: new Date().toISOString() },
      { key: 'admin_review_required', value: adminReview, updated_at: new Date().toISOString() },
      { key: 'image_optimization', value: image, updated_at: new Date().toISOString() },
      { key: 'seo', value: seo, updated_at: new Date().toISOString() },
      { key: 'show_elite_featured', value: showElite, updated_at: new Date().toISOString() },
      { key: 'admin_email', value: adminEmail, updated_at: new Date().toISOString() },
      { key: 'admin_password', value: adminPassword, updated_at: new Date().toISOString() },
    ]);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  return (
    <div className="space-y-6 text-slate-800">
      <SectionHeader
        title="Engine Settings"
        subtitle="Global configuration for the automation engine. Applies to all connectors and every listing flowing through the pipeline."
        right={
          <div className="flex items-center gap-3">
            {saved && <Badge tone="success">Saved</Badge>}
            <Button onClick={save}><Save className="h-4 w-4" /> Save changes</Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Sliders className="h-4 w-4 text-emerald-600" /><div className="text-sm font-bold text-slate-900">Duplicate detection</div></div>
          <div className="text-xs text-slate-500 font-semibold mb-2">Auto-merge threshold</div>
          <input type="range" min={0.5} max={1} step={0.01} value={threshold} onChange={(e) => setThreshold(parseFloat(e.target.value))} className="w-full accent-emerald-600 cursor-pointer" />
          <div className="flex justify-between text-[11px] font-mono font-bold mt-1">
            <span className="text-slate-400">0.50</span>
            <span className="text-emerald-700 font-extrabold">{threshold.toFixed(2)}</span>
            <span className="text-slate-400">1.00</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-sm font-bold text-slate-900 mb-4">Publishing</div>
          <Toggle label="Auto-publish new listings" hint="Skip admin review when confidence is high" value={autoPublish} onChange={setAutoPublish} />
          <Toggle label="Require admin review" hint="Every new listing enters review before going live" value={adminReview} onChange={setAdminReview} />
        </Card>

        <Card className="p-6">
          <div className="text-sm font-bold text-slate-900 mb-4">Image optimization</div>
          <Toggle label="Generate WebP" value={image.webp} onChange={(v) => setImage({ ...image, webp: v })} />
          <Toggle label="Generate AVIF" value={image.avif} onChange={(v) => setImage({ ...image, avif: v })} />
          <Toggle label="Generate thumbnails" value={image.thumbnail} onChange={(v) => setImage({ ...image, thumbnail: v })} />
          <Toggle label="Blur placeholders (lazy loading)" value={image.blur_placeholder} onChange={(v) => setImage({ ...image, blur_placeholder: v })} />
        </Card>

        <Card className="p-6">
          <div className="text-sm font-bold text-slate-900 mb-4">Homepage Featured Collections</div>
          <Toggle
            label="Show Elite Featured Businesses Section"
            hint="Display/Hide the Elite Premium Collection section on the website homepage"
            value={showElite}
            onChange={(v) => {
              setShowElite(v);
              localStorage.setItem('localhub_show_elite_featured', JSON.stringify(v));
              void supabase.from('settings').upsert({ key: 'show_elite_featured', value: v });
            }}
          />
        </Card>

        <Card className="p-6">
          <div className="text-sm font-bold text-slate-900 mb-4">Super Admin Security Credentials</div>
          <div className="space-y-3 text-xs font-semibold">
            <div>
              <label className="text-slate-500 font-bold block mb-1">Super Admin Email ID</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none"
              />
            </div>
            <div>
              <label className="text-slate-500 font-bold block mb-1">Super Admin Password</label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="Enter new strong password..."
                className="w-full h-9 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none"
              />
            </div>
            <div className="text-[10px] text-emerald-700 font-bold italic">
              🔒 Super Admin credentials are encrypted and protected by strict auth guards.
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5 mt-6">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Raw settings</div>
        <pre className="text-[11px] text-slate-100 font-mono bg-slate-900 border border-slate-800 rounded-xl p-3 overflow-x-auto scrollbar-thin">
{JSON.stringify(rows, null, 2)}
        </pre>
      </Card>
    </div>
  );
}

function Toggle({ label, hint, value, onChange }: { label: string; hint?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-b-0 cursor-pointer">
      <div className="min-w-0">
        <div className="text-xs font-bold text-slate-800">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 font-medium mt-0.5">{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative h-6 w-11 rounded-full transition shrink-0 ${value ? 'bg-emerald-600' : 'bg-slate-200'}`}
      >
        <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition shadow-sm" style={{ left: value ? '22px' : '2px' }} />
      </button>
    </label>
  );
}
